import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Button, Card } from 'react-bootstrap';
import ActivityChart30d from 'Utils/ActivityChart30d';
import StatTile from 'Utils/StatTile';
import RunningJobRow from 'Utils/RunningJobRow';
import SortableTh, { sortRows } from 'Utils/SortableTh';
import AppContext from 'Utils/AppContext';
import { engineLabels, EngineIcon } from 'Utils';
import { useActivityWindowDays, ActivityWindowPicker, windowLabel } from 'Utils/ActivityWindow';
import { workItemLabel } from 'Utils/nomenclature';
import { BRAND, INK, FONT_MONO } from 'Utils/styleTokens';
import { listDiscoverJobsPage, startDiscoveryWithRule, cancelDiscoverJob, getJobStatus } from 'DataDiscovery/discoverApi';
import { ensureConnectionTested } from 'Utils/connectionGate';
import { getLandscapeHomeSummary } from 'SensitiveDataLandscape/homeSummaryApi';
import { buildCensus, censusCounts, discoveredWithin } from 'Utils/sourceCensus';
import { getLandscapeEntities } from 'SensitiveDataLandscape/landscapeEntitiesApi';
import AdoptedFromBadge from 'DataSources/AdoptedFromBadge';
import CancelJobModal from 'DiscoveryReports/ReportPanel/CancelJobModal';
import { useConfirm } from 'Utils/ConfirmDialog';
import { useCanEdit, READ_ONLY_TITLE } from 'Auth/permissions';
import { fmtDate, fmtDateTime } from 'Utils/formatDate';

// One series: every discovery run, whatever it ended as. Failures are not tracked as their own
// bar — a failed run is still a run, and it is called out in the day's drilldown instead.
const ACTIVITY_SERIES = [{ key: 'run', label: 'Discovery runs', color: BRAND.blue }];

// Ledger opens newest-discovery-first, which is also what the leftmost column sorts on.
const DEFAULT_LEDGER_SORT = { key: 'lastDiscovery', dir: 'desc' };

const fmt = (n) => Number(n || 0).toLocaleString();

const compact = (n) => {
  const v = Number(n || 0);
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return String(v);
};

const STATUS_PILLS = {
  DISCOVERED: { label: 'Discovered', className: 'sg-pill--info' },
  ERROR: { label: 'Failed', className: 'sg-pill--danger' },
  SCANNING: { label: 'Scanning…', className: 'sg-pill--info' },
};

// Ledger order: running/queued rows pin to the top, then most recent discovery first.
// Sources with neither a discovery nor a job are dropped (they live on the landscape
// home, not this ledger). Exported for tests.
/**
 * Route to an in-flight run's results, or undefined when there is nothing to open yet.
 *
 * The ledger row offered only Cancel while a run was in flight, so the sole way to reach the
 * findings of a twelve-hour scan was to stop it (C2D-1200). The report handles a RUNNING job
 * already — it polls status and pulls the stored partial snapshot — so the row just has to
 * link to it. A PENDING run is withheld deliberately: nothing has been sampled, and a link to
 * an empty report reads as "no findings" rather than "not started".
 */
export const runningResultsRoute = (job) => {
  if (!job || job.status !== 'RUNNING' || job.id == null) return undefined;
  return `/results/${encodeURIComponent(job.id)}`;
};

export const orderLedgerRows = (sources, jobsBySource) => {
  const jobActiveFor = (s) => {
    const job = jobsBySource[s.id];
    return job && (job.status === 'RUNNING' || job.status === 'PENDING');
  };
  const discoveryTs = (s) => (s.latestDiscoveryAt ? Date.parse(s.latestDiscoveryAt) || 0 : 0);
  return sources
    .filter((s) => s.latestDiscoveryAt != null || jobsBySource[s.id])
    .sort((a, b) => {
      const runDelta = (jobActiveFor(b) ? 1 : 0) - (jobActiveFor(a) ? 1 : 0);
      if (runDelta !== 0) return runDelta;
      return discoveryTs(b) - discoveryTs(a);
    });
};

/**
 * Discover landscape — mockup 5a. One row per discovered source with entity chips,
 * sampled-items progress, findings (instance counts from the entity roll-up), status
 * from the latest discover job, and Re-run / Results actions. KPI strip + 30-day
 * discovery-activity chart above.
 */
function DiscoverLandscape() {
  const { confirm, confirmDialog } = useConfirm();
  const history = useHistory();
  const { setNotification, connections } = useContext(AppContext) || {};
  const canEdit = useCanEdit();

  // Summary sources carry no origin — join provenance from the globally-loaded connections.
  const provenanceOf = (source) => {
    const c = (connections || []).find((x) => String(x.id) === String(source.id)) || {};
    return { registeredServiceId: c.registeredServiceId || null, registeredServiceName: c.registeredServiceName || null };
  };
  const [summary, setSummary] = useState(null);
  const [jobsBySource, setJobsBySource] = useState({});
  const [events, setEvents] = useState([]);
  const [findingsBySource, setFindingsBySource] = useState({});
  const [entitiesBySource, setEntitiesBySource] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  // Cancel is a destructive action (it aborts an in-flight scan), so it goes through a
  // confirmation dialog rather than firing on the first click (C2D-994). Holds the
  // {source, job} pending confirmation; null = dialog closed.
  const [cancelTarget, setCancelTarget] = useState(null);
  // Header-click sort. Defaults to the date column (newest first); null = the raw ledger order
  // (running rows pinned, then recency).
  const [ledgerSort, setLedgerSort] = useState(DEFAULT_LEDGER_SORT);
  // Trailing window every panel on this page reports over (shared across the landscape pages).
  const [windowDays, setWindowDays] = useActivityWindowDays();

  const load = useCallback(async () => {
    const [summaryRes, jobsRes, tilesRes] = await Promise.allSettled([
      getLandscapeHomeSummary(),
      listDiscoverJobsPage(0, 200),
      getLandscapeEntities(),
    ]);
    if (summaryRes.status === 'fulfilled') setSummary(summaryRes.value);
    if (jobsRes.status === 'fulfilled') {
      const jobs = jobsRes.value.discoverJobs || [];
      const latest = {};
      jobs.forEach((job) => {
        if (job.dataSourceId != null && !latest[job.dataSourceId]) latest[job.dataSourceId] = job;
      });
      // The LIST returns counters only as fresh as the platform's own worker poll (30s).
      // For rows we render as running, fetch the single-job endpoint — it LIVE-SYNCS an
      // active job with the worker — so the progress bar moves in near-real-time.
      const running = Object.values(latest).filter(
        (job) => job && (job.status === 'RUNNING' || job.status === 'PENDING')
      );
      await Promise.all(
        running.map(async (job) => {
          try {
            // Counters only — getJobStatus maps `status` to UI vocabulary, which would break
            // the raw-enum RUNNING/PENDING checks here; the next tick's list pass settles state.
            const live = await getJobStatus(job.id);
            if (live.workItemsCompleted != null) job.workItemsCompleted = live.workItemsCompleted;
            if (live.workItemsTotal != null) job.workItemsTotal = live.workItemsTotal;
            if (live.workItemKind != null) job.workItemKind = live.workItemKind;
          } catch (e) {
            // Stale list data still renders; the next tick retries.
          }
        })
      );
      setJobsBySource(latest);
      // Enriched so the activity chart's bar drilldown can list each run: source name,
      // status, and a link to its results.
      setEvents(
        jobs.map((job) => ({
          ts: job.startedAt || job.finishedAt,
          key: 'run',
          id: job.id,
          dataSourceId: job.dataSourceId,
          kind: 'Discovery',
          name: job.dataSourceName || (job.dataSourceId != null ? `#${job.dataSourceId}` : '—'),
          status: job.status,
          // A FAILED run still links to its results. A failure at 90% after twelve hours has
          // real findings behind it, and withholding the link told the user the run simply
          // never happened. The error travels alongside so the report can call it out rather
          // than present partial results as complete.
          to: job.id != null ? `/results/${encodeURIComponent(job.id)}` : undefined,
          error: job.status === 'FAILED' ? job.errorMessage || undefined : undefined,
        }))
      );
    }
    if (tilesRes.status === 'fulfilled') {
      const totals = {};
      const entities = {};
      tilesRes.value.forEach((tile) => {
        tile.sources.forEach((source) => {
          totals[source.dataSourceId] = (totals[source.dataSourceId] || 0) + source.instanceCount;
          (entities[source.dataSourceId] = entities[source.dataSourceId] || []).push(tile.entityType);
        });
      });
      setFindingsBySource(totals);
      setEntitiesBySource(entities);
    }
  }, []);

  // Poll so running discoveries clear and the ledger updates without a manual reload.
  // 5s while anything is running — that's what keeps the running row's progress bar moving
  // (the job DTO's workItems counters refresh on the platform's worker polls); 30s when idle.
  const anyJobActive = Object.values(jobsBySource).some(
    (job) => job && (job.status === 'RUNNING' || job.status === 'PENDING')
  );
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    const timer = setInterval(load, anyJobActive ? 5000 : 30000);
    return () => clearInterval(timer);
  }, [load, anyJobActive]);

  // Runs the actual cancellation once the user confirms in the dialog.
  const performCancel = async (source, job) => {
    if (!job || job.id == null) return;
    setCancellingId(source.id);
    try {
      await cancelDiscoverJob(job.id);
      if (setNotification) {
        setNotification({ header: 'Discovery cancelled', body: `Cancelled the discovery for ${source.name}.` });
      }
      await load();
    } catch (e) {
      console.log('Cancel discovery failed', e);
      if (setNotification) {
        setNotification({ header: 'Cancel failed', body: e.message || String(e) });
      }
    }
    setCancellingId(null);
  };

  const confirmCancel = async () => {
    const target = cancelTarget;
    if (!target) return;
    await performCancel(target.source, target.job);
    setCancelTarget(null);
  };

  const statusOf = (source) => {
    const job = jobsBySource[source.id];
    if (job && (job.status === 'RUNNING' || job.status === 'PENDING')) return STATUS_PILLS.SCANNING;
    if (job && job.status === 'FAILED') return STATUS_PILLS.ERROR;
    return STATUS_PILLS.DISCOVERED;
  };

  const chipsOf = (source) => {
    const fromRollup = entitiesBySource[source.id];
    if (fromRollup && fromRollup.length > 0) return fromRollup;
    return (source.latestFindings || []).map((f) => f.entity).filter(Boolean);
  };

  // The same census every landscape header counts with (Utils/sourceCensus): no landscape=none
  // rows, no platform-managed rows, no fan-out sub-sources — so this page's "N sources" is the
  // Workflow's and the Secure landscape's N.
  const census = buildCensus(summary?.sources || [], connections);
  const sources = census.visible;
  const censusTotals = censusCounts(sources);
  const ledgerAccessors = {
    name: (s) => s.name,
    type: (s) => engineLabels[s.engine] || s.engine,
    sampled: (s) => jobsBySource[s.id]?.workItemsCompleted ?? null,
    findings: (s) => findingsBySource[s.id] ?? null,
    status: (s) => statusOf(s).label,
    lastDiscovery: (s) => (s.latestDiscoveryAt ? Date.parse(s.latestDiscoveryAt) || null : null),
  };
  // A source scanning right now has no fresh "last discovery" yet — under the date sort it would
  // sink to the bottom, which is the opposite of what someone watching a run wants. So running and
  // queued rows stay pinned to the top while the ledger is in its date order (the default, and what
  // sort = null falls back to); an explicit sort on another column is left exactly as asked for.
  const isRunning = (s) => {
    const job = jobsBySource[s.id];
    return !!job && (job.status === 'RUNNING' || job.status === 'PENDING');
  };
  const sorted = sortRows(orderLedgerRows(sources, jobsBySource), ledgerSort, ledgerAccessors);
  const dateOrdered = !ledgerSort || ledgerSort.key === DEFAULT_LEDGER_SORT.key;
  const rows = dateOrdered
    ? [...sorted.filter(isRunning), ...sorted.filter((s) => !isRunning(s))]
    : sorted;
  // All three panels report over the SAME window and the same set of sources, so their source
  // counts reconcile: sources with findings + clean sources = sources discovered in the window.
  const cutoff = Date.now() - windowDays * 86400000;
  const inWindow = (ts) => {
    const t = ts ? Date.parse(ts) : NaN;
    return Number.isFinite(t) && t >= cutoff;
  };
  const windowSources = sources.filter((s) => inWindow(s.latestDiscoveryAt));
  const discoveredCount = discoveredWithin(sources, windowDays);
  const windowFindings = windowSources.reduce((a, s) => a + (findingsBySource[s.id] || 0), 0);
  const findingSourceCount = windowSources.filter((s) => (findingsBySource[s.id] || 0) > 0).length;
  const cleanSourceCount = Math.max(0, discoveredCount - findingSourceCount);
  const windowEvents = events.filter((e) => inWindow(e.ts));
  const runsInWindow = windowEvents.length;
  const runSourceCount = new Set(windowEvents.map((e) => e.dataSourceId).filter((v) => v != null)).size;

  const rerun = async (source) => {
    const ok = await confirm({
      title: `Re-run discovery of "${source.name}"?`,
      body: 'Samples the source again with its last sampling rule. Previous findings are kept for comparison.',
      confirmLabel: 'Re-run discovery',
    });
    if (!ok) return;
    setBusyId(source.id);
    try {
      const gate = await ensureConnectionTested(Number(source.id), {
        history,
        setNotification,
        name: source.name,
      });
      if (!gate.ok) {
        setBusyId(null);
        return;
      }
      // Re-run with the same sampling rule as the source's latest run when we know it;
      // startDiscoveryWithRule falls back to the default rule otherwise.
      const job = await startDiscoveryWithRule(
        Number(source.id),
        jobsBySource[source.id]?.samplingRuleTemplateId ?? null
      );
      // Optimistically drop in a running row so it appears the moment discovery starts,
      // instead of waiting for the next 30s poll. The poll reconciles it with real progress.
      setJobsBySource((prev) => ({
        ...prev,
        [source.id]: {
          id: job && job.id != null ? job.id : prev[source.id]?.id,
          dataSourceId: Number(source.id),
          status: (job && job.status) || 'PENDING',
          workItemsTotal: 0,
          workItemsCompleted: 0,
        },
      }));
      if (setNotification) {
        setNotification({ header: 'Discovery started', body: `Re-running discovery for ${source.name}.` });
      }
    } catch (e) {
      console.log('Re-run discovery failed', e);
      if (setNotification) {
        setNotification({ header: 'Discovery failed to start', body: e.message || String(e) });
      }
    }
    setBusyId(null);
  };

  return (
    <div>
      {confirmDialog}
      <div className='d-flex align-items-center mb-2'>
        <div className='ml-auto'>
          <ActivityWindowPicker days={windowDays} onChange={setWindowDays} />
        </div>
      </div>
      <ActivityChart30d
        title={`Discovery activity, ${windowLabel(windowDays)}`}
        series={ACTIVITY_SERIES}
        events={events}
        days={windowDays}
        drilldown
      />
      <div className='mb-3' style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
        <StatTile
          dot={INK[500]}
          label='Sources'
          value={fmt(censusTotals.total)}
          sub={`${fmt(censusTotals.everDiscovered)} discovered · ${fmt(censusTotals.neverDiscovered)} never discovered`}
          testId='discover-stat-total'
        />
        <StatTile
          dot={BRAND.blue}
          label={`Discovered, ${windowLabel(windowDays)}`}
          value={fmt(discoveredCount)}
          sub={`of ${fmt(censusTotals.everDiscovered)} ever discovered`}
          testId='discover-stat-sources'
        />
        <StatTile
          dot={BRAND.violet}
          label={`Findings, ${windowLabel(windowDays)}`}
          value={compact(windowFindings)}
          sub={`across ${fmt(findingSourceCount)} source${findingSourceCount === 1 ? '' : 's'} · ${fmt(cleanSourceCount)} clean`}
          testId='discover-stat-findings'
        />
        <StatTile
          dot={BRAND.cyan}
          label={`Runs, ${windowLabel(windowDays)}`}
          value={fmt(runsInWindow)}
          sub={`discovery runs across ${fmt(runSourceCount)} source${runSourceCount === 1 ? '' : 's'}`}
          testId='discover-stat-runs'
        />
      </div>
      <Card>
        <Card.Body>
          {/* textAlign left beats the global `.App { text-align: center }` inherit — word
              cells read left-justified; the trailing actions cell overrides back to right. */}
          <div className='sg-table-scroll'>
          <table className='w-100' style={{ fontSize: 13, textAlign: 'left' }} data-testid='discover-ledger'>
            <thead>
              <tr className='sg-eyebrow' style={{ textAlign: 'left' }}>
                <SortableTh label='Last discovery' sortKey='lastDiscovery' sort={ledgerSort} onSort={setLedgerSort} />
                <SortableTh label='Data source' sortKey='name' sort={ledgerSort} onSort={setLedgerSort} />
                <SortableTh label='Type' sortKey='type' sort={ledgerSort} onSort={setLedgerSort} className='sg-col--p3' />
                <SortableTh label='Items sampled' sortKey='sampled' sort={ledgerSort} onSort={setLedgerSort} className='sg-col--p2' />
                <SortableTh label='Findings' sortKey='findings' sort={ledgerSort} onSort={setLedgerSort} />
                <SortableTh label='Status' sortKey='status' sort={ledgerSort} onSort={setLedgerSort} />
                <th style={{ padding: '8px 10px' }} className='sg-col-actions' aria-label='Actions' />
              </tr>
            </thead>
            <tbody>
              {rows.map((source) => {
                const job = jobsBySource[source.id];
                const running = job && (job.status === 'RUNNING' || job.status === 'PENDING');
                if (running) {
                  return (
                    <RunningJobRow
                      key={source.id}
                      testId={`discover-running-${source.id}`}
                      nameNode={
                        <Link
                          to={`/landscape/riskprofile/${encodeURIComponent(source.id)}`}
                          style={{ fontFamily: FONT_MONO, color: BRAND.blue }}
                        >
                          {source.name}
                        </Link>
                      }
                      leading={
                        <span className='sg-small' style={{ fontFamily: FONT_MONO, color: 'var(--ink-500)' }}>
                          {fmtDate(source.latestDiscoveryAt)}
                        </span>
                      }
                      engine={source.engine}
                      jobType='Discovery'
                      status={job.status === 'PENDING' ? 'QUEUED' : 'SCANNING'}
                      resultsTo={runningResultsRoute(job)}
                      completed={job.workItemsCompleted}
                      total={job.workItemsTotal}
                      unit={workItemLabel(job.workItemKind, 'tables')}
                      totalCols={7}
                      cancelling={cancellingId === source.id}
                      onCancel={() => setCancelTarget({ source, job })}
                    />
                  );
                }
                const pill = statusOf(source);
                const chips = chipsOf(source).slice(0, 4);
                const extra = chipsOf(source).length - chips.length;
                const itemsTotal = job?.workItemsTotal || 0;
                const itemsDone = job?.workItemsCompleted || 0;
                const findings = findingsBySource[source.id];
                return (
                  <tr key={source.id} style={{ borderTop: '1px solid var(--ink-100)', verticalAlign: 'top' }}>
                    <td
                      style={{ padding: '9px 10px', whiteSpace: 'nowrap', fontFamily: FONT_MONO, color: 'var(--ink-500)' }}
                      className='sg-small'
                      title={source.latestDiscoveryAt
                        ? fmtDateTime(source.latestDiscoveryAt)
                        : undefined}
                    >
                      {fmtDate(source.latestDiscoveryAt)}
                    </td>
                    <td style={{ padding: '9px 10px', maxWidth: 320 }}>
                      <Link
                        to={`/landscape/riskprofile/${encodeURIComponent(source.id)}`}
                        style={{ fontFamily: FONT_MONO, color: BRAND.blue }}
                      >
                        {source.name}
                      </Link>
                      <AdoptedFromBadge {...provenanceOf(source)} />
                      {chips.length > 0 && (
                        <div className='d-flex flex-wrap mt-1' style={{ gap: 4 }}>
                          {chips.map((entity) => (
                            <span key={entity} className='sg-tag' style={{ fontSize: 11 }}>
                              {entity}
                            </span>
                          ))}
                          {extra > 0 && (
                            <span className='sg-count-chip' style={{ fontSize: 11 }}>
                              +{extra}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className='sg-col--p3' style={{ padding: '9px 10px', whiteSpace: 'nowrap' }}>
                      <EngineIcon engine={source.engine} /> {engineLabels[source.engine] || source.engine}
                    </td>
                    <td className='sg-col--p2' style={{ padding: '9px 10px', minWidth: 140 }}>
                      {itemsTotal > 0 ? (
                        <div>
                          <div
                            style={{
                              height: 6,
                              borderRadius: 'var(--r-full)',
                              background: INK[100],
                              overflow: 'hidden',
                              marginBottom: 3,
                            }}
                          >
                            <div
                              style={{
                                width: `${Math.min(100, (itemsDone / itemsTotal) * 100)}%`,
                                height: '100%',
                                background: BRAND.blue,
                              }}
                            />
                          </div>
                          <span className='sg-small' style={{ fontFamily: FONT_MONO, color: 'var(--ink-500)' }}>
                            {fmt(itemsDone)} / {fmt(itemsTotal)}
                          </span>
                        </div>
                      ) : (
                        <span style={{ color: 'var(--ink-500)' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '9px 10px', fontFamily: FONT_MONO }}>
                      {findings != null ? fmt(findings) : '—'}
                    </td>
                    <td style={{ padding: '9px 10px' }}>
                      <span className={`sg-pill ${pill.className}`} style={{ fontSize: 11.5 }}>
                        {pill.label}
                      </span>
                    </td>
                    <td className='sg-col-actions' style={{ padding: '9px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        className='mr-2'
                        title={canEdit ? 'Re-run discovery' : READ_ONLY_TITLE}
                        disabled={busyId === source.id || !canEdit}
                        onClick={() => rerun(source)}
                        data-testid={`discover-rerun-${source.id}`}
                      >
                        ↻ Re-run
                      </Button>
                      <Button
                        size='sm'
                        style={{ background: BRAND.blue, borderColor: BRAND.blue }}
                        disabled={source.latestDiscoveryId == null}
                        onClick={() => history.push(`/results/${encodeURIComponent(source.latestDiscoveryId)}`)}
                      >
                        Results →
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className='text-center sg-small' style={{ padding: 24, color: 'var(--ink-500)' }}>
                    No discoveries yet. Use Actions, then New Discovery, to run your first scan.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </Card.Body>
      </Card>
      <CancelJobModal
        show={!!cancelTarget}
        setShow={() => setCancelTarget(null)}
        jobId={cancelTarget?.job?.id}
        cancelJobHandler={confirmCancel}
        fetchingData={cancellingId != null}
      />
    </div>
  );
}

export default DiscoverLandscape;
